import { useState } from 'react'
import type { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useEditorStore, clearHistory, undo, redo } from '../store/editor'
import { useAuthStore } from '../store/auth'
import { useSceneData } from '../hooks/useSceneData'
import { useHistory } from '../hooks/useHistory'
import { getScene } from '../scenes'
import { DurationField, ColorField, Field, TextInput } from './editor-controls'
import { api, ApiError } from '../api/client'

// 右侧编辑器面板：渲染当前场景的 EditorComponent，控件经 useSceneData().update 改 data。
// 顶部「✨ AI 填充」按钮调 /scenes/:id/autofill，用 LLM 按提示生成场景数据并载入。
export default function EditorPanel({
  open = false,
  onClose,
}: {
  open?: boolean
  onClose?: () => void
}) {
  const { t } = useTranslation()
  const sceneId = useEditorStore((s) => s.sceneId)
  const duration = useEditorStore((s) => s.duration)
  const loadScene = useEditorStore((s) => s.loadScene)
  const { data, update } = useSceneData()
  const replaceData = useEditorStore((s) => s.replaceData)
  const { canUndo, canRedo } = useHistory()
  const user = useAuthStore((s) => s.user)
  const openAuthModal = useAuthStore((s) => s.openAuthModal)
  const [filling, setFilling] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [aiHint, setAiHint] = useState('')
  const [aiError, setAiError] = useState('')
  const [aiChars, setAiChars] = useState(0)

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `visword-${sceneId || 'scene'}.json`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  const importJson = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        replaceData(JSON.parse(String(reader.result)))
      } catch {
        window.alert(t('editorPanel.jsonParseFail'))
      }
    }
    reader.readAsText(file)
    e.target.value = '' // 允许重选同一文件
  }

  const plugin = sceneId ? getScene(sceneId) : undefined
  const Editor = plugin?.EditorComponent

  const onAutofill = async () => {
    if (!sceneId || !aiHint.trim()) return
    // 覆盖确认：AI 填充会整体替换当前数据
    if (!window.confirm(t('editorPanel.aiOverwriteConfirm'))) return
    setFilling(true)
    setAiError('')
    setAiChars(0)
    try {
      const { data: filled } = await api.autofillStream(sceneId, aiHint.trim(), (_delta, total) => {
        setAiChars(total)
      })
      loadScene(sceneId, filled, duration)
      clearHistory()
      setAiOpen(false)
      setAiHint('')
    } catch (e: unknown) {
      setAiError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : t('editorPanel.aiFail'))
    } finally {
      setFilling(false)
      setAiChars(0)
    }
  }

  return (
    <>
      <div className={`editor-backdrop${open ? ' visible' : ''}`} onClick={onClose} aria-hidden="true" />
      <aside className={`editor-panel${open ? ' open' : ''}`}>
      <div className="panel-label editor-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{t('studio.editor')}</span>
        <div className="editor-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => undo()}
            disabled={!canUndo}
            title={t('editorPanel.undo')}
            aria-label="undo"
          >
            ↶
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => redo()}
            disabled={!canRedo}
            title={t('editorPanel.redo')}
            aria-label="redo"
          >
            ↷
          </button>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => (user ? setAiOpen(!aiOpen) : openAuthModal())}
            disabled={filling || !sceneId}
            title={user ? t('editorPanel.aiFill') : t('editorPanel.aiLoginRequired')}
          >
            {filling ? '◐ …' : t('editorPanel.aiFill')}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm editor-close"
            onClick={onClose}
            aria-label="close"
            title={t('editorPanel.close')}
          >
            ✕
          </button>
        </div>
      </div>
      {aiOpen && (
        <div className="ai-fill-area" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <TextInput
            value={aiHint}
            onChange={setAiHint}
            placeholder="如：2026 全球 AI 公司市值排名"
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onAutofill}
              disabled={filling || !aiHint.trim()}
              style={{ flex: 1 }}
            >
              {filling ? t('editorPanel.aiGenerating', { count: aiChars }) : '✨ 生成'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => { setAiOpen(false); setAiHint(''); setAiError('') }}
            >
              {t('common.cancel')}
            </button>
          </div>
          {aiError && <div className="error-box" style={{ fontSize: 12, margin: 0 }}>{aiError}</div>}
        </div>
      )}
      <DurationField />
      <Field label={t('editorPanel.bgColor')}>
        <ColorField value={(data as any).bgColor ?? ''} onChange={(c) => update((dd) => { dd.bgColor = c })} />
        {(data as any).bgColor && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ marginTop: 6, width: '100%' }}
            onClick={() => update((dd) => { dd.bgColor = undefined })}
          >
            {t('editorPanel.resetBg')}
          </button>
        )}
      </Field>
      <Field label={t('editorPanel.data')}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={exportJson} style={{ flex: 1 }}>
            {t('editorPanel.exportJson')}
          </button>
          <label
            className="btn btn-outline btn-sm"
            style={{ flex: 1, textAlign: 'center', cursor: 'pointer', marginBottom: 0 }}
          >
            {t('editorPanel.importJson')}
            <input type="file" accept="application/json,.json" hidden onChange={importJson} />
          </label>
        </div>
      </Field>
      {Editor ? (
        <Editor data={data} update={update} />
      ) : (
        <div className="editor-hint">No editor for this scene.</div>
      )}
      </aside>
    </>
  )
}
